import { Injectable } from '@angular/core';
import { AbstractControl, ValidationErrors, ValidatorFn, Validators } from '@angular/forms';
import { LocalizationService } from '@abp/ng.core';
import { GenericValidator } from '../generic-form/model/generic-validator';
import { ValidationTypes } from '../generic-form/enums/validation-types';
import { localizeWithFallback } from '../utils/localization-utils';

export interface EditorValidationResult {
  valid: boolean;
  msg: string;
}

export type EditorValidator = (value: any, args?: any) => EditorValidationResult;

@Injectable({
  providedIn: 'root'
})
export class ValidationConverterService {
  constructor(private readonly localizationService: LocalizationService) {}

  toSlickGridValidator(
    validators: GenericValidator[],
    required: boolean,
    controlType: string
  ): EditorValidator {
    return (value: any, args?: any): EditorValidationResult => {
      const isTextField = controlType === 'textbox' || controlType === 'textarea';
      let processedValue = value;

      if (isTextField && typeof value === 'string') {
        processedValue = value.trim();
        if (args?.editor && processedValue !== value) {
          args.editor.setValue(processedValue);
        }
      }

      if (required && (processedValue === null || processedValue === undefined || processedValue === '')) {
        return {
          valid: false,
          msg: localizeWithFallback(this.localizationService, 'LanguageService::DefaultErrorRequired')
        };
      }

      for (const validator of validators) {
        const result = this.validateSingleForSlickGrid(validator, processedValue, controlType, args);
        if (!result.valid) {
          return result;
        }
      }

      return { valid: true, msg: '' };
    };
  }

  private validateSingleForSlickGrid(
    validator: GenericValidator,
    value: any,
    _controlType: string,
    args?: any
  ): EditorValidationResult {
    switch (validator.key) {
      case ValidationTypes.MinLength:
        if (value !== '' && value !== null && value !== undefined && typeof value === 'string' && value.length < validator.value) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 
              'LanguageService::PleaseEnterAtLeast',
              String(validator.value)
            )
          };
        }
        break;

      case ValidationTypes.MaxLength:
        if (typeof value === 'string' && value.length > validator.value) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 
              'LanguageService::PleaseEnterAtMost',
              String(validator.value)
            )
          };
        }
        break;

      case ValidationTypes.Min:
        if (typeof value === 'number' && value < validator.value) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 
              'LanguageService::PleaseEnterAValueGreaterOrEqualTo',
              String(validator.value)
            )
          };
        }
        break;

      case ValidationTypes.Max:
        if (typeof value === 'number' && value > validator.value) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 
              'LanguageService::PleaseEnterAValueLowerOrEqualTo',
              String(validator.value)
            )
          };
        }
        break;

      case ValidationTypes.Email:
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (value !== '' && value !== null && value !== undefined && !emailRegex.test(value)) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 'LanguageService::DefaultErrorEmail')
          };
        }
        break;

      case ValidationTypes.Pattern:
        try {
          const regex = new RegExp(validator.value);
          if (value !== '' && value !== null && value !== undefined && !regex.test(String(value))) {
            return {
              valid: false,
              msg: validator.message
                ? localizeWithFallback(this.localizationService, validator.message)
                : localizeWithFallback(this.localizationService, 'LanguageService::DefaultErrorPattern')
            };
          }
        } catch (e) {
          console.error('Invalid regex pattern:', validator.value, e);
        }
        break;

        case ValidationTypes.Decimal: {
          
          let scale = 2;
          let totalPrecision: number | undefined;

          if (typeof validator.value === 'object' && validator.value !== null) {
            scale = (validator.value as any).scale ?? 2;
            totalPrecision = (validator.value as any).precision;
          } else {
            scale = validator.value ?? 2;
          }

          const stringValue = String(value).replace(',', '.');
          const parts = stringValue.split('.');
          const integerPart = parts[0].replace('-', '');
          const decimalPart = parts[1] || '';

          if (value !== '' && value !== null && value !== undefined) {
            if (decimalPart.length > scale) {
              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 
                  'LanguageService::InvalidDecimalScale', String(scale)
                )
              };
            }

            if (totalPrecision !== undefined) {
              const maxIntegerDigits = Math.max(totalPrecision - scale, 0);
              if (integerPart.length > maxIntegerDigits) {
                return {
                  valid: false,
                  msg: localizeWithFallback(this.localizationService, 
                    'LanguageService::InvalidDecimalPrecision', String(totalPrecision)
                  )
                };
              }
            }

            const decimalRegex = /^-?\d+([.,]\d+)?$/;
            if (!decimalRegex.test(stringValue)) {
              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 'LanguageService::InvalidDecimalFormat')
              };
            }
          }
          break;
        }

      case ValidationTypes.Time:
        const timeRegex = /^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/;
        if (value !== '' && value !== null && value !== undefined && !timeRegex.test(String(value))) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 'LanguageService::PleaseEnterAValidTime')
          };
        }
        break;

    case ValidationTypes.Date:
      if (value !== '' && value !== null && value !== undefined) {
        
        if (value instanceof Date && !isNaN(value.getTime())) {
          break;
        }
        
        const dateRegex = /^\d{4}-(0?[1-9]|1[0-2])-(0?[1-9]|[12]\d|3[01])$/;

        if (!dateRegex.test(String(value))) {
          return {
            valid: false,
            msg: localizeWithFallback(this.localizationService, 'LanguageService::InvalidDateFormat')
          };
        }
      }
      break;

      case ValidationTypes.EqualOrLessThanField:
        if (args?.item && validator.value) {
          const otherValue = args.item[validator.value];
          if (value && otherValue) {
            const comparison = this.compareValues(value, otherValue);
            if (comparison !== null && comparison > 0) {
            const translatedField = validator.fieldLocalization
              ? localizeWithFallback(this.localizationService, validator.fieldLocalization)
              : validator.value;

              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 
                  'LanguageService::MustBeEqualOrLessThan',
                  translatedField
                )
              };
            }
          }
        }
        break;

      case ValidationTypes.LessThanField:
        if (args?.item && validator.value) {
          const otherValue = args.item[validator.value];
          if (value && otherValue) {
            const comparison = this.compareValues(value, otherValue);
            if (comparison !== null && comparison >= 0) {
            const translatedField = validator.fieldLocalization
              ? localizeWithFallback(this.localizationService, validator.fieldLocalization)
              : validator.value;

              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 
                  'LanguageService::MustBeLessThan',
                  translatedField
                )
              };
            }
          }
        }
        break;

      case ValidationTypes.EqualOrGreaterThanField:
        if (args?.item && validator.value) {
          const otherValue = args.item[validator.value];
          if (value && otherValue) {
            const comparison = this.compareValues(value, otherValue);
            if (comparison !== null && comparison < 0) {
              const translatedField = validator.fieldLocalization
                ? localizeWithFallback(this.localizationService, validator.fieldLocalization)
                : validator.value;

              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 
                  'LanguageService::MustBeEqualOrGreaterThan',
                  translatedField
                )
              };
            }
          }
        }
        break;

      case ValidationTypes.GreaterThanField:
        if (args?.item && validator.value) {
          const otherValue = args.item[validator.value];
          if (value && otherValue) {
            const comparison = this.compareValues(value, otherValue);
            if (comparison !== null && comparison <= 0) {
              const translatedField = validator.fieldLocalization
                ? localizeWithFallback(this.localizationService, validator.fieldLocalization)
                : validator.value;

              return {
                valid: false,
                msg: localizeWithFallback(this.localizationService, 
                  'LanguageService::MustBeGreaterThan',
                  translatedField
                )
              };
            }
          }
        }
        break;
    }

    return { valid: true, msg: '' };
  }

  toAngularValidators(validators: GenericValidator[]): ValidatorFn[] {
    const auxValidators: ValidatorFn[] = [];

    for (const validator of validators) {
      switch (validator.key) {
        case ValidationTypes.Email:
          auxValidators.push(Validators.email);
          break;

        case ValidationTypes.Max:
          auxValidators.push(Validators.max(validator.value));
          break;

        case ValidationTypes.MaxLength:
          auxValidators.push(Validators.maxLength(validator.value));
          break;

        case ValidationTypes.Min:
          auxValidators.push(Validators.min(validator.value));
          break;

        case ValidationTypes.MinLength:
          auxValidators.push(Validators.minLength(validator.value));
          break;

        case ValidationTypes.Pattern:
          auxValidators.push(Validators.pattern(validator.value));
          break;

        case ValidationTypes.Required:
          auxValidators.push(Validators.required);
          break;

        case ValidationTypes.RequiredTrue:
          auxValidators.push(Validators.requiredTrue);
          break;

        case ValidationTypes.Decimal:
         auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            const hasConfigError =
              typeof validator.value === 'object' &&
              validator.value !== null &&
              (validator.value as any).configError === true;

            if (hasConfigError) {
              const rawScale = (validator.value as any)?.rawScale;
              const rawPrecision = (validator.value as any)?.rawPrecision;
              const detail = `scale=${rawScale}, precision=${rawPrecision}`;
              return { decimal: { messageKey: 'InvalidDecimalConfig', value: detail } };
            }

            if (control.value === null || control.value === '' || control.value === undefined) {
              return null;
            }

            let scale = 2;
            let totalPrecision: number | undefined;

            if (typeof validator.value === 'object' && validator.value !== null) {
              scale = (validator.value as any).scale ?? 2;
              totalPrecision = (validator.value as any).precision;
            } else {
              scale = validator.value ?? 2;
            }

            const stringValue = String(control.value).replace(',', '.');
            const parts = stringValue.split('.');
            const integerPart = parts[0].replace('-', '');
            const decimalPart = parts[1] || '';

            if (decimalPart.length > scale) {
              return { decimal: { messageKey: 'InvalidDecimalScale', value: scale } };
            }

            if (totalPrecision !== undefined) {
              const maxIntegerDigits = Math.max(totalPrecision - scale, 0);
              if (integerPart.length > maxIntegerDigits) {
                return { decimal: { messageKey: 'InvalidDecimalPrecision', value: totalPrecision } };
              }
            }

            const decimalRegex = /^-?\d+([.,]\d+)?$/;
            return decimalRegex.test(stringValue)
              ? null
              : { decimal: { messageKey: 'InvalidDecimalFormat', value: null } };
          });
          break;

        case ValidationTypes.Time:
          auxValidators.push(Validators.pattern(/^([01]\d|2[0-3]):([0-5]\d):([0-5]\d)$/));
          break;

        case ValidationTypes.Date:
          auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            if (!control.value) {
              return null;
            }

            if (control.value instanceof Date && !isNaN(control.value.getTime())) {
              return null;
            }

            const isoDateRegex =
              /^\d{4}-\d{2}-\d{2}(T\d{2}:\d{2}(:\d{2}(\.\d{3})?)?(Z|[+-]\d{2}:\d{2})?)?$/;

            if (!isoDateRegex.test(String(control.value))) {
              return { date: true };
            }

            const parsedDate = new Date(control.value);
            if (isNaN(parsedDate.getTime())) {
              return { date: true };
            }

            return null;
          });
          break;

        case ValidationTypes.EqualOrLessThanField:
          auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            if (!control.parent || !control.value) {
              return null;
            }

            const otherControl = control.parent.get(validator.value);
            if (!otherControl || !otherControl.value) {
              return null;
            }

            const currentValue = this.normalizeDateTimeValue(control.value);
            const otherValue = this.normalizeDateTimeValue(otherControl.value);

            if (currentValue !== null && otherValue !== null) {
              if (currentValue > otherValue) {
                return {
                  equalOrLessThanField: {
                    field: validator.value,
                    actualValue: control.value,
                    requiredEqualOrLessThan: otherControl.value
                  }
                };
              }
            }

            return null;
          });
          break;

        case ValidationTypes.LessThanField:
          auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            if (!control.parent || !control.value) {
              return null;
            }

            const otherControl = control.parent.get(validator.value);
            if (!otherControl || !otherControl.value) {
              return null;
            }

            const currentValue = this.normalizeDateTimeValue(control.value);
            const otherValue = this.normalizeDateTimeValue(otherControl.value);

            if (currentValue !== null && otherValue !== null) {
              if (currentValue >= otherValue) {
                return {
                  lessThanField: {
                    field: validator.value,
                    actualValue: control.value,
                    requiredLessThan: otherControl.value
                  }
                };
              }
            }

            return null;
          });
          break;

        case ValidationTypes.EqualOrGreaterThanField:
          auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            if (!control.parent || !control.value) {
              return null;
            }

            const otherControl = control.parent.get(validator.value);
            if (!otherControl || !otherControl.value) {
              return null;
            }

            const currentValue = this.normalizeDateTimeValue(control.value);
            const otherValue = this.normalizeDateTimeValue(otherControl.value);

            if (currentValue !== null && otherValue !== null) {
              if (currentValue < otherValue) {
                return {
                  equalOrGreaterThanField: {
                    field: validator.value,
                    actualValue: control.value,
                    requiredEqualOrGreaterThan: otherControl.value
                  }
                };
              }
            }

            return null;
          });
          break;

        case ValidationTypes.GreaterThanField:
          auxValidators.push((control: AbstractControl): ValidationErrors | null => {
            if (!control.parent || !control.value) {
              return null;
            }

            const otherControl = control.parent.get(validator.value);
            if (!otherControl || !otherControl.value) {
              return null;
            }

            const currentValue = this.normalizeDateTimeValue(control.value);
            const otherValue = this.normalizeDateTimeValue(otherControl.value);

            if (currentValue !== null && otherValue !== null) {
              if (currentValue <= otherValue) {
                return {
                  greaterThanField: {
                    field: validator.value,
                    actualValue: control.value,
                    requiredGreaterThan: otherControl.value
                  }
                };
              }
            }

            return null;
          });
          break;
      }
    }

    return auxValidators;
  }
  private normalizeDateTimeValue(value: any): number | null {
    if (!value) return null;

    if (value instanceof Date) {
      return this.getValidDateTime(value);
    }

    if (typeof value !== 'string') {
      return null;
    }

    return this.parseDateOnlyValue(value) ?? this.parseIsoDateTimeValue(value);
  }

  private parseDateOnlyValue(value: string): number | null {
    const dateOnlyMatch = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
    if (!dateOnlyMatch) {
      return null;
    }

    const year = Number.parseInt(dateOnlyMatch[1], 10);
    const month = Number.parseInt(dateOnlyMatch[2], 10);
    const day = Number.parseInt(dateOnlyMatch[3], 10);
    const date = new Date(Date.UTC(year, month - 1, day));

    if (
      date.getUTCFullYear() !== year ||
      date.getUTCMonth() !== month - 1 ||
      date.getUTCDate() !== day
    ) {
      return null;
    }

    return date.getTime();
  }

  private parseIsoDateTimeValue(value: string): number | null {
    if (!value.includes('T')) {
      return null;
    }

    try {
      return this.getValidDateTime(new Date(value));
    } catch {
      return null;
    }
  }

  private getValidDateTime(value: Date): number | null {
    return Number.isNaN(value.getTime()) ? null : value.getTime();
  }
  
  private compareValues(value1: any, value2: any): number | null {
    const normalizeTime = (val: any): string => {
      const str = String(val);
      if (str.includes('T')) {
        const timePart = str.split('T')[1];
        return timePart ? timePart.substring(0, 5) : str;
      }

        if (/^\d{2}:\d{2}:\d{2}/.test(str)) {
        return str.substring(0, 5);
      }
      return str;
    };

    const str1 = normalizeTime(value1);
    const str2 = normalizeTime(value2);

    const timeRegex = /^([01]?[0-9]|2[0-3]):([0-5][0-9])$/;
    const isTime1 = timeRegex.test(str1);
    const isTime2 = timeRegex.test(str2);

    if (isTime1 && isTime2) {
      return str1.localeCompare(str2);
    }

    const date1 = new Date(value1);
    const date2 = new Date(value2);

    if (!isNaN(date1.getTime()) && !isNaN(date2.getTime())) {
      return date1.getTime() - date2.getTime();
    }

    const num1 = Number.parseFloat(String(value1));
    const num2 = Number.parseFloat(String(value2));

    if (!Number.isNaN(num1) && !Number.isNaN(num2)) {
      return num1 - num2;
    }

    return null;
  }
}
